import { ErrorAlert } from "@components/ErrorAlert";
import { queriesRequirementDefinitions } from "@query";
import {
  useWSModal,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSText
} from "@wingspanhq/fe-component-library";
import { RequirementDefinition } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { DefaultActionDetails } from "../components/DefaultActionDetails";

export interface DeleteRequirementModalProps {
  requirementDefinition: RequirementDefinition;
  engagementLength: number;
  onClose: () => void;
  onSuccess: (requirement: RequirementDefinition) => void;
}

export const DeleteRequirementModal: React.FC<DeleteRequirementModalProps> = ({
  requirementDefinition,
  engagementLength,
  onClose,
  onSuccess
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [deleteRequirementDefinition, deleteRequirementDefinitionMeta] =
    queriesRequirementDefinitions.deleteRequirementDefinition.useMutation();

  const handleDelete = async () => {
    await deleteRequirementDefinition(
      { requirementDefinitionId: requirementDefinition.id! },
      {
        onSuccess: () => {
          openSnackbar({
            theme: "dark",
            type: "success",
            message: "Requirement deleted"
          });
          onClose();
          onSuccess(requirementDefinition);
        }
      }
    );
  };

  return (
    <>
      <WSText.ParagraphSm mb="L" weight="book">
        Are you sure you want to delete this requirement?
      </WSText.ParagraphSm>
      <DefaultActionDetails
        engagementLength={engagementLength}
        requirementDefinition={requirementDefinition}
      />

      <WSAlert
        icon="alert"
        my="2XL"
        size="M"
        theme="warning"
        title="Deleting requirement"
      >
        Deletion is a permanent action, it cannot be restored.
      </WSAlert>

      <ErrorAlert
        context="UpdateRequirement"
        error={deleteRequirementDefinitionMeta.error}
        mb="XL"
      />

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Secondary"
          },
          {
            label: "Delete requirement",
            loading: deleteRequirementDefinitionMeta.isLoading,
            onAsyncClick: handleDelete,
            kind: "Primary",
            destructive: true
          }
        ]}
        sortButtons={false}
      />
    </>
  );
};

export const useDeleteRequirementModal = () => {
  return useWSModal(DeleteRequirementModal, {
    title: "Delete requirement",
    size: "M"
  });
};
